import { Button, FormGroup, MenuItem } from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import type {
  ExtraFieldTypeSelect,
  ExtraFieldTypeSelectChoice,
} from "@package/ui-gql/index-data-access";
import { get, set } from "lodash";
import { useMemo } from "react";
import { isConditionFalsey } from "../service-data-form-utils";
import type { ServiceDataFormFieldBaseProps } from "./ServiceDataFormFieldEmpty";

type ServiceDataFormFieldSelectProps = ServiceDataFormFieldBaseProps & {
  field: ExtraFieldTypeSelect;
};

function ServiceDataFormFieldSelectContent({
  data,
  field,
  onSourceChanged,
}: ServiceDataFormFieldSelectProps) {
  const activeItem = useMemo<ExtraFieldTypeSelectChoice | undefined>(() => {
    const choiceKey =
      get(data, field.source) ??
      field.defaultValue ??
      field.defaultChoice ??
      undefined;
    if (choiceKey) {
      return field.choices.find((choice) => choice.id === choiceKey);
    }

    return undefined;
  }, [
    data,
    field.choices,
    field.defaultChoice,
    field.defaultValue,
    field.source,
  ]);
  const disabled = useMemo<boolean>(
    () => !!field.condition && isConditionFalsey(field.condition, data),
    [data, field.condition],
  );

  return (
    <FormGroup disabled={disabled} label={field.label}>
      <Select<ExtraFieldTypeSelectChoice>
        activeItem={activeItem}
        disabled={disabled}
        fill
        filterable={false}
        items={field.choices}
        itemRenderer={(choice, { handleClick, handleFocus, modifiers }) => (
          <MenuItem
            active={modifiers.active}
            disabled={modifiers.disabled}
            key={choice.id}
            onClick={handleClick}
            onFocus={handleFocus}
            roleStructure="listoption"
            text={choice.name}
          />
        )}
        popoverProps={{ minimal: true }}
        onItemSelect={(choice) =>
          onSourceChanged((draft) => {
            set(draft, field.source, choice.id);
          })
        }
      >
        <Button
          alignText="left"
          disabled={disabled}
          fill
          minimal
          outlined
          rightIcon="caret-down"
          text={activeItem?.name ?? "Select an Item"}
        />
      </Select>
    </FormGroup>
  );
}

export function ServiceDataFormFieldSelect({
  field,
  onSourceChanged,
  data,
}: ServiceDataFormFieldBaseProps) {
  if (field.__typename === "ExtraFieldTypeSelect") {
    return (
      <ServiceDataFormFieldSelectContent
        field={field}
        data={data}
        onSourceChanged={onSourceChanged}
      />
    );
  }

  return null;
}
